import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import ActionMenuItem from '../../components/ActionMenu/ActionMenuItem';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import DateControl from '../../components/Fields/DateControl';
import EmailField from '../../components/Fields/EmailField';
import Field from '../../components/Fields/Field';
import FieldLabel from '../../components/Fields/FieldLabel';
import Form from '../../components/Fields/Form';
import SelectListField from '../../components/Fields/SelectListField';
import TextboxControl from '../../components/Fields/TextboxControl';
import TextboxField from '../../components/Fields/TextboxField';
import { FieldConfig, FormConfig } from '../../components/Fields/Types';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from '../../components/Spinner/SpinnerOverlay';
import { HorizontalTabs } from '../../components/Tabs/HorizontalTabs';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { DataResult, Result } from '../../types/Result';
import Unauthorized from '../Authentication/Unauthorized';
import User_popup_ChangePassword from './Popups/User_popup_ChangePassword';
import User_popup_ChangeProfilePicture from './Popups/User_popup_ChangeProfilePicture';
import User_popup_OutOfOffice from './Popups/User_popup_OutOfOffice';
import Employee_tab_BusinessPartners from './Tabs/Employee_tab_BusinessPartners';
import Employee_tab_FunctionalRoles from './Tabs/Employee_tab_FunctionalRoles';
import Employee_tab_Divisions from './Tabs/Employee_tab_Divisions';
import User_tab_ApplicationRoles from './Tabs/User_tab_ApplicationRoles';

interface EmployeeEditModel {
    id: number

    loginName: string
    firstName: string
    preposition?: string
    genderId: number
    lastName: string
    email: string
    language: string
    userId?: number
    departmentId?: number
    referenceCode: string
    fileUrl: string
    beginDate: string
    endDate?: string

    concurrencyId: string
}

const EmployeeDetailsView: React.FC = () => {
    const [serverErrors, setServerErrors] = useState<Result>({ isSuccesful: true, messages: [] });
    const { handleResult, handleException } = useNotifications();
    const [itemsRefreshCounter, setItemsRefreshCounter] = useState<number>(0)
    const [showChangeProfilePicturePopup, setShowChangeProfilePicturePopup] = useState<boolean>(false);
    const [showOutOfOfficePopup, setShowOutOfOfficePopup] = useState<boolean>(false);
    const [showChangePasswordPopup, setShowChangePasswordPopup] = useState<boolean>(false);
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);
    const { user, changeProfilePicture } = useAuth();
    const navigate = useNavigate();
    const [formData, setFormData] = useState<EmployeeEditModel>();

    let employeeId;
    let idParams: IdParams = useParams();
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        employeeId = parseInt(idParams.id);
    }

    const departments = useSelectListOptions('api/v1/selectlist/Departments', '-- Select department');
    const genders = useSelectListOptions('api/v1/selectlist/Genders', '-- Select gender');
    const languages = useSelectListOptions('api/v1/selectlist/Languages', '-- Select language');


    useEffect(() => {
        if (employeeId !== undefined) {
            DataService.getById<EmployeeEditModel>('api/v1/Employees/', employeeId)
                .then(response => {
                    setFormData(response);
                })
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [employeeId]);

    function submit() {
        if (formData) {
            DataService
                .update<EmployeeEditModel>(`api/v1/Employees/`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        setFormData(response.data);
                })
                .catch(e => handleException(e));
        }
    }

    function resetPassword() {
        if (formData)
            DataService.post<Result>('api/v1/Users/' + formData.userId + '/resetPassword', {})
                .then(response => {
                    handleResult(response);
                });
    }

    function onProfilePictureChanged(thumbnailUrl?: string) {
        if (formData && thumbnailUrl) {
            formData.fileUrl = thumbnailUrl;
            setFormData({ ...formData });
            if (user && formData.userId == user.userId) {
                changeProfilePicture(thumbnailUrl);
            }
        }
    }

    let fields: FieldConfig[] = [
        { name: 'firstName', label: 'Name', labelSize: 3, validationRules: [{ type: 'isRequired' }, { type: 'isMaxLength', maxLength: 250 }] },
        { name: 'prePosition', label: 'Name', labelSize: 3, validationRules: [{ type: 'isMaxLength', maxLength: 250 }] },
        { name: 'lastName', label: 'Name', labelSize: 3, validationRules: [{ type: 'isRequired' }, { type: 'isMaxLength', maxLength: 250 }] },
        { name: 'genderId', label: 'Gender', labelSize: 3 },
        { name: 'departmentId', label: 'Department', labelSize: 3 },
        { name: 'language', label: 'Language', labelSize: 3 },
        { name: 'loginName', label: 'Login name', labelSize: 3, validationRules: [{ type: 'isRequired' }, { type: 'isMaxLength', maxLength: 250 }] },
        { name: 'email', label: 'Email', labelSize: 3, validationRules: [{ type: 'isRequired' }, { type: 'isMaxLength', maxLength: 250 }] },
        { name: 'beginDate', label: 'Active from', labelSize: 3, validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
        { name: 'endDate', label: 'Active Until', labelSize: 'auto', validationRules: [{ type: 'isDate' }] },
    ];

    const formConfig: FormConfig = {
        fields: fields,
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);
    const { isAuthorizedFor } = useAuth();


    if (employeeId === undefined) {
        if (!isAuthorizedFor(ApplicationFunctions.CreateEmployee)) {
            return (<Unauthorized />);
        }
    }
    else if (!isAuthorized) {
        return (<Unauthorized />);
    }

    if (!formData)
        return <SpinnerOverlay />

    const formActions =
        <ActionMenu title="Actions">
            <ActionMenuItem width={250} icon="fa fa-id-badge" title="Set profile picture" appFunction={ApplicationFunctions.ChangeProfilePicture} onClick={() => { setShowChangeProfilePicturePopup(true); }} />
            <ActionMenuItem icon="fa fa-sign-out" title="Out of Office" appFunction={ApplicationFunctions.SetOutOfOffice} onClick={() => { setShowOutOfOfficePopup(true); }} />
            <ActionMenuItem icon="fa fa-edit" title="Reset password and unlock" appFunction={ApplicationFunctions.ResetPassword} onClick={() => { resetPassword(); }} />
            {formData.userId === user?.userId && <ActionMenuItem icon="fa fa-edit" title="Change password" onClick={() => { setShowChangePasswordPopup(true); }} />}
            {formData.userId && (<ActionMenuItem icon="fa fa-user" title="Goto user" appFunction={ApplicationFunctions.ViewUser} onClick={() => { navigate('/plm/UserManagement/User/' + formData.userId); }} />)}
        </ActionMenu>

    return (
        <div className='main-content'>
            <Form
                title={'Employee'}
                actions={formActions}
                footer={(
                    <Row>
                        <Col md='3' className='hidden-md'>
                            <ButtonBar offset={0} >
                            </ButtonBar>
                        </Col>
                        <Col md='5'>
                            <ButtonBar offset={4}>
                                <Authorize applicationFunction={ApplicationFunctions.UpdateUser} >
                                    <SaveButton  {...getSubmitButtonProps()} />
                                </Authorize>
                            </ButtonBar>
                        </Col>
                        <Col md='4' className='hidden-md'>
                            <ButtonBar offset={0} >
                            </ButtonBar>
                        </Col>
                    </Row>
                )}>
                <Col md='3'>
                    <img src={formData.fileUrl} style={{ width: '100%' }} />
                </Col>
                <Col md='9'>
                    <Field {...getLabelProps('firstName')}>
                        <TextboxControl {...getControlProps('firstName')} />
                        <TextboxControl {...getControlProps('prePosition')} width={'40%'} />
                        <TextboxControl {...getControlProps('lastName')} />
                    </Field>
                    <SelectListField {...getFieldProps('genderId')} options={genders} />
                    <SelectListField {...getFieldProps('departmentId')} options={departments} />
                    <SelectListField {...getFieldProps('language')} options={languages} />
                    <TextboxField {...getFieldProps('loginName')} />
                    <EmailField {...getFieldProps('email')} />
                    <Field {...getLabelProps('beginDate')}>
                        <DateControl {...getControlProps('beginDate')} />
                        <span style={{ padding: '7px 15px' }}><FieldLabel {...getLabelProps('endDate')} /></span>
                        <DateControl {...getControlProps('endDate')} />
                    </Field>
                </Col>
            </Form>
            {employeeId && formData.userId && (
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <HorizontalTabs
                                id='main-tabs'
                                defaultActiveKey={'tab_applicationroles'}
                                tabs={[
                                    {
                                        title: 'Application roles',
                                        eventKey: 'tab_applicationroles',
                                        component: <User_tab_ApplicationRoles userId={formData.userId} />
                                    },
                                    {
                                        title: 'Workflow roles',
                                        eventKey: 'tab_functionalRoles',
                                        component: <Employee_tab_FunctionalRoles employeeId={employeeId} />
                                    },
                                    {
                                        title: 'Divisions',
                                        eventKey: 'tab_divisions',
                                        component: <Employee_tab_Divisions employeeId={employeeId} />
                                    },
                                    {
                                        title: 'Business-partners',
                                        eventKey: 'tab_businessPartners',
                                        component: <Employee_tab_BusinessPartners employeeId={employeeId} />
                                    },
                                ]}
                            />
                        </Col>
                    </Row>
                    {showChangeProfilePicturePopup && <User_popup_ChangeProfilePicture userId={formData.userId} show={showChangeProfilePicturePopup} onHide={(thumbnailUrl?: string) => { setShowChangeProfilePicturePopup(false); onProfilePictureChanged(thumbnailUrl); }} />}
                    {showChangePasswordPopup && <User_popup_ChangePassword userId={formData.userId} username={formData.loginName} show={showChangePasswordPopup} onHide={() => { setShowChangePasswordPopup(false); }} />}
                    {showOutOfOfficePopup && <User_popup_OutOfOffice userId={formData.userId} show={showOutOfOfficePopup} onHide={() => { setShowOutOfOfficePopup(false); setItemsRefreshCounter(itemsRefreshCounter + 1) }} />}
                </Container>
            )}
        </div>
    );
}
export default EmployeeDetailsView;